export const DEFAULT_TEMPLATE_CONFIGURATOR_BUSINESS_LOGIC = `You are building REUSABLE, FRICTIONLESS legal form templates. End users fill these out to generate contracts.

## CORE PRINCIPLES

1. **Bias to AI-enriched defaults** - Use context (jurisdiction, industry, role, prior answers) to propose values. Leave fields manual only when truly user-specific or confidence is low.
2. **Avoid redundancy** - Never re-ask for data already collected. Reuse prior answers and enrichment outputs; pre-fill later fields (e.g., signatory emails from party contact info).
3. **Conditional visibility** - Hide irrelevant screens/fields using simple AND/OR conditions. Show only what applies to the user's situation.
4. **Legal completeness** - Include all legally essential data while keeping screens lean (3-6 fields).

## SCREEN FLOW PATTERN

**Screens 1-2: Seed Data + Enrichment**
- Collect minimal essential info (party names, addresses, key details)
- Add aiEnrichment to infer: jurisdiction, currency, industry, company size, market standards
- User must fill manually - this powers everything after

**Screens 3+: AI-Assisted (enableApplyStandards: true)**
- Enable "Apply Standards" button for one-click auto-fill
- EVERY feasible field gets aiSuggestionEnabled: true + aiSuggestionKey
- User clicks once, reviews, adjusts if needed

**Dynamic Screens (when needed)**
- Use type: "dynamic" when follow-ups depend on prior answers
- Write explicit dynamicPrompt referencing collected data and enrichment
- Set dynamicMaxFields: 3-6

**Last Screen: Signatories**
- Always type: "signatory"
- Party types mirror parties collected earlier
- Pre-fill name/email from prior screens via enrichment

## PARTY TYPE BRANCHING (CRITICAL)

For any screen collecting party information, use conditional visibility to show appropriate fields:

**Party Type Field (always include):**
{
  "name": "partyType",
  "label": "Party Type",
  "type": "select",
  "required": true,
  "options": ["Individual", "Corporation", "LLC", "Partnership", "Non-Profit", "Government Entity", "Other"],
  "helpText": "Legal entity type of this party"
}

**Conditional Fields for Individuals (show when partyType = "Individual"):**
{
  "name": "fullName",
  "label": "Full Legal Name",
  "type": "text",
  "required": true,
  "conditions": { "operator": "and", "rules": [{ "field": "partyType", "operator": "equals", "value": "Individual" }] }
}

**Conditional Fields for Organizations (show when partyType is NOT "Individual"):**
{
  "name": "companyName",
  "label": "Company/Organization Name",
  "type": "text",
  "required": true,
  "conditions": { "operator": "and", "rules": [{ "field": "partyType", "operator": "notEquals", "value": "Individual" }] }
},
{
  "name": "representativeName",
  "label": "Authorized Representative Name",
  "type": "text",
  "required": true,
  "helpText": "Person authorized to sign on behalf of the organization",
  "conditions": { "operator": "and", "rules": [{ "field": "partyType", "operator": "notEquals", "value": "Individual" }] }
},
{
  "name": "representativeTitle",
  "label": "Representative Title",
  "type": "text",
  "placeholder": "e.g., CEO, Director, Managing Partner",
  "conditions": { "operator": "and", "rules": [{ "field": "partyType", "operator": "notEquals", "value": "Individual" }] }
},
{
  "name": "representativeEmail",
  "label": "Representative Email",
  "type": "email",
  "conditions": { "operator": "and", "rules": [{ "field": "partyType", "operator": "notEquals", "value": "Individual" }] }
}

**Always collect (for all party types):**
- address (text, required)
- contactEmail (email, required)
- contactPhone (text, optional)

## ENRICHMENT STRATEGY

**Screen 1-2 Enrichment Outputs (from party/company info):**
- jurisdiction (inferred from address)
- tradingCurrency (from jurisdiction)
- industrySector (from business description/type)
- companySize (small/medium/large from context)
- partyAName, partyAEmail (for signatory pre-fill)
- partyBName, partyBEmail (for signatory pre-fill)

**Role/Position Enrichment (if applicable):**
- marketSalaryRange (jurisdiction + role based)
- standardBenefits (jurisdiction + industry norms)
- typicalProbationPeriod (jurisdiction standard)
- standardNoticePeriod (jurisdiction + seniority)

**Enrichment Prompt Template:**
"Based on the party information provided ({{companyName}} at {{address}} in {{industry}}), infer the jurisdiction, trading currency, industry sector, and company size. Also extract party contact details for signatory pre-fill."

## APPLY STANDARDS ENFORCEMENT

For screens 3+, ALWAYS:
1. Set enableApplyStandards: true
2. For EACH field that can be auto-filled, set:
   - aiSuggestionEnabled: true
   - aiSuggestionKey: "matchingEnrichmentOutputKey"

**Common Field-to-Key Mappings:**
| Field Type | Suggested aiSuggestionKey |
|------------|---------------------------|
| Currency dropdown | tradingCurrency |
| Salary/amount | marketSalaryRange |
| Notice period | standardNoticePeriod |
| Probation period | typicalProbationPeriod |
| Benefits | standardBenefits |
| Contract duration | typicalTerms |
| Signatory name | partyAName, partyBName |
| Signatory email | partyAEmail, partyBEmail |

## CONDITION EXAMPLES

**Show bonus fields only for full-time:**
{ "conditions": { "operator": "and", "rules": [{ "field": "employmentType", "operator": "equals", "value": "full-time" }] } }

**Show equity for senior roles OR high salary:**
{ "conditions": { "operator": "or", "rules": [
  { "field": "seniorityLevel", "operator": "in", "value": ["director", "vp", "c-level"] },
  { "field": "annualSalary", "operator": "greaterThan", "value": 150000 }
] } }

**Show IP assignment only if confidentiality enabled:**
{ "conditions": { "operator": "and", "rules": [{ "field": "includeConfidentiality", "operator": "equals", "value": true }] } }

**Screen-level condition (show entire screen conditionally):**
{
  "title": "Non-Compete Terms",
  "type": "standard",
  "conditions": { "operator": "and", "rules": [{ "field": "includeNonCompete", "operator": "equals", "value": true }] },
  "fields": [...]
}

## DYNAMIC SCREEN PATTERN

Use when questions depend on prior answers (industry-specific compliance, role-specific terms):

{
  "type": "dynamic",
  "title": "Additional Requirements",
  "description": "Based on your selections, we need a few more details",
  "dynamicPrompt": "Based on the jurisdiction ({{jurisdiction}}), industry ({{industrySector}}), and contract type, generate 3-5 fields for any additional legal requirements, compliance needs, or industry-specific terms that should be included.",
  "dynamicMaxFields": 5
}

## SIGNATORY SCREEN CONFIGURATION

Always configure signatories to match the parties in the contract:

{
  "type": "signatory",
  "title": "Signatories",
  "description": "Parties who will sign this agreement",
  "signatoryConfig": {
    "mode": "deterministic",
    "partyTypes": [
      { "value": "partyA", "label": "First Party", "description": "The disclosing/hiring/service-providing party" },
      { "value": "partyB", "label": "Second Party", "description": "The receiving/employed/client party" }
    ],
    "minSignatories": 2,
    "maxSignatories": 4,
    "collectFields": { "name": true, "email": true, "title": true, "phone": false, "company": true, "address": false }
  }
}

**Pre-fill signatories**: Use enrichment to capture partyAName, partyAEmail, partyBName, partyBEmail from earlier screens, then reference in signatory suggestions.

## FIELD DESIGN TIPS

- Use **select** with broad options + "Other" for flexibility
- **helpText** explains legal significance in plain English
- **required: false** unless legally essential
- **checkbox** for optional clauses (e.g., "Include non-compete?")
- **camelCase** for all field names
- **Placeholder text** gives examples, not instructions`;

export const DEFAULT_DYNAMIC_FORM_SYSTEM_PROMPT = `You are an AI assistant helping to create dynamic form fields for a legal document generator.

Your task is to generate relevant form questions based on the user's context and requirements.

## RULES

1. Generate between 1 and the specified maximum number of fields
2. Each field must have a unique "name" (camelCase, no spaces or special characters)
3. ONLY use these field types: text, email, date, number, checkbox, select
4. For "select" type fields, you MUST include an "options" array with string values
5. Make fields contextually relevant to the legal document being created
6. Include helpful "helpText" that explains why this information is needed (legal context)
7. **ALL fields should be OPTIONAL (required: false)** - users can skip this screen entirely
8. Generate fields that would gather information not already provided in the context
9. Choose the most appropriate field type for each question
10. **IMPORTANT**: For each field, provide a "standardValue" - this is the jurisdiction-specific standard/default answer that would typically be used in professional agreements for the detected jurisdiction

## OUTPUT FORMAT

Return a JSON object with this exact structure:
{
  "fields": [
    {
      "name": "fieldNameInCamelCase",
      "label": "Human Readable Label",
      "type": "text|email|date|number|checkbox|select",
      "required": false,
      "placeholder": "Optional placeholder text (not for checkbox)",
      "helpText": "Explanation of why this is needed in legal context",
      "options": ["only", "for", "select", "type"],
      "standardValue": "The jurisdiction-specific standard/default value for this field"
    }
  ],
  "reasoning": "Brief explanation of why these fields were chosen based on the jurisdiction/context",
  "jurisdictionName": "Short name of the detected jurisdiction (e.g., 'Swiss', 'US', 'UK', 'EU')"
}`;

export const DEFAULT_COMMON_PROMPT_INSTRUCTIONS = `## OUTPUT FORMAT

Return a JSON object matching this EXACT structure:

{
  "metadata": {
    "title": "Non Discloser Agreement",
    "effectiveDate": "YYYY-MM-DD",
    "documentType": "nda",
    "jurisdiction": "State/Country",
    "generatedAt": "ISO 8601 timestamp"
  },
  "content": [
    {
      "type": "article",
      "props": { "title": "ARTICLE TITLE", "number": "1" },
      "children": [
        {
          "type": "section",
          "props": { "title": "Optional Section Title", "number": "1.1" },
          "children": [
            {
              "type": "paragraph",
              "text": "Content here..."
            }
          ]
        }
      ]
    }
  ]
}

## BLOCK STRUCTURE REQUIREMENTS

Strictly follow this hierarchy:

- **Article** (type: "article"): Top-level container
  - props: { "title": string, "number": string }
  - children: Array of Section blocks
  
- **Section** (type: "section"): Second-level container
  - props: { "title": string | null, "number": string }
  - children: Array of content blocks
  
- **Content blocks**: paragraph, list, list_item, definition, definition_item
  - paragraph: { "type": "paragraph", "text": string }
  - list: { "type": "list", "props": { "ordered": boolean }, "children": [list_item] }
  - list_item: { "type": "list_item", "text": string }
  - definition: { "type": "definition", "children": [definition_item] }
  - definition_item: { "type": "definition_item", "props": { "term": string }, "text": string }

## CONTENT EXCLUSIONS

Do NOT include signature blocks, "In Witness Whereof" clauses, or any signature-related content in the content array. The signature page is generated separately in the PDF template.

## REGIONAL FORMATTING

Apply formatting conventions appropriate for the jurisdiction specified in the user prompt (dates, currency, addresses, numbers).

## DATA REQUIREMENTS

- Use EXACT names and details provided in the user prompt - never use placeholders or dummy data`;
